import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";

import { FormView } from "../Form";
import {
    EXPRESSION_SOURCE,
    setDesignFaultSink,
} from "../shared/design-fault-reporting";
import { FORM_LAYOUT } from "../shared/form-layouts";
import { formReducer, types } from "../shared/formReducer";
import { PreviewModeContext, RenderMode } from "~/context/PreviewModeContext";
import { i18n } from "~/localization/i18n";

import { getAllowedOrigins, isAllowedOrigin } from "./previewOrigin";
import { PreviewAppFooter } from "./PreviewAppFooter";
import { PreviewAppHeader } from "./PreviewAppHeader";

import "./PreviewForm.css";

/**
 * /preview — the iframe mount loaded by Viedoc.Designer for live Me preview.
 *
 * Contract (see docs/features/2-preview-route.md):
 *   - No `RequireAuth`, no OIDC. The iframe holds no patient session.
 *   - No `fetch`, no API call, no STS token. QuestModel arrives via
 *     `window.postMessage` from the Designer parent only.
 *   - Iframe → Parent: `viedoc.preview.ready` on mount, and a throttled
 *     `viedoc.preview.activity` on user interaction — the Designer preview
 *     tab hosts the IdP inactivity tracker, which cannot see into this
 *     cross-origin frame, so working in the previewed form must announce
 *     itself or the tab idles out mid-test. Nothing else leaves the frame —
 *     every preview control (language, viewport, form layout) lives in the
 *     Designer parent, and inbound payload fields are NEVER echoed back.
 *   - Parent → Iframe: `viedoc.preview.render-result` (the questModel, plus
 *     the sidebar's current overrides riding along) / `render-error` /
 *     `override-change` (identifier-model and dependency overrides on their
 *     own, when the designer presses Apply or Reset all) /
 *     `form-layout` (the sidebar's "Item groups on one page" toggle —
 *     'paged' | 'all-groups', re-asserted after every render).
 *   - Origin validated in both directions against an allow-list sourced
 *     from `VITE_DESIGNER_ORIGINS`. Outbound `targetOrigin` is the most
 *     recent valid inbound origin — never `"*"`, never `window.parent.origin`.
 *   - File-touching widgets short-circuit via `PreviewModeContext` so any
 *     accidental render of a FileUpload / DrawingPad triggers zero I/O.
 *
 * Operational note: `frame-ancestors` CANNOT be set from a <meta> tag
 * (UAs ignore it per spec). The reverse proxy hosting Me.Web must set
 * `Content-Security-Policy: frame-ancestors <designer origins>` and drop
 * `X-Frame-Options: DENY` for the /preview path. See task doc for details.
 */

const SUPPORTED_PAYLOAD_VERSIONS = [1];
const READY_KIND = "viedoc.preview.ready";
const RENDER_RESULT_KIND = "viedoc.preview.render-result";
const RENDER_ERROR_KIND = "viedoc.preview.render-error";
const OVERRIDE_CHANGE_KIND = "viedoc.preview.override-change";
const FORM_LAYOUT_KIND = "viedoc.preview.form-layout";
const ACTIVITY_KIND = "viedoc.preview.activity";

// One activity message per interval is plenty: the parent's tracker samples
// activity every couple of seconds and the idle timeout is measured in
// minutes.
const ACTIVITY_THROTTLE_MS = 5000;

const INBOUND_KINDS = new Set([
    RENDER_RESULT_KIND,
    RENDER_ERROR_KIND,
    OVERRIDE_CHANGE_KIND,
    FORM_LAYOUT_KIND,
]);

// Envelope guards shared by every inbound branch. `target` discriminates the
// two renderers in the Designer's unified preview; its absence means "for
// everyone".
const versionOf = (data) => data.payloadVersion ?? 1;
const isSupportedVersion = (data) =>
    SUPPORTED_PAYLOAD_VERSIONS.includes(versionOf(data));
const isForMe = (data) => !data.target || data.target === "me";

// The form's language is a full BCP-47 tag (e.g. "en-US") but i18next's
// whitelist in src/localization/languages.js uses primary subtags
// ("en", "sv"). Strip the region/script before calling changeLanguage so
// the system texts actually resolve.
function primaryLanguageTag(tag) {
    if (typeof tag !== "string" || !tag) return "";
    return tag.split("-")[0].toLowerCase();
}

// Flip system texts (button labels, validation messages) to match the form's
// language. No-op when the tag is empty or already on the primary subtag.
function switchSystemLanguage(tag) {
    const primary = primaryLanguageTag(tag);
    if (primary) {
        i18n.changeLanguage(primary);
    }
}

const initialState = {
    form: { itemGroups: [] },
};

// Identity of a thrown expression: the same condition is re-evaluated on every
// render and a broken function on every keystroke, and the panel must show one
// row for it, not one per tick.
const faultKey = (fault) =>
    `${fault.source ?? ""}|${fault.oid ?? ""}|${fault.expression ?? ""}`;

// One row shape for both kinds of problem, worded the way the Clinic preview
// words its rows so a designer switching renderer reads the same list:
// `OID (label) — Source: message`. The message is the sandbox's own error text,
// unedited, because that is what Clinic shows for the same expression.
function toPanelRow(kind, oid, label, source, message, expression) {
    const item = label ? `${oid} (${label})` : oid;
    const prefix = source ? `${item} — ${source}: ` : `${item}: `;
    return {
        key: `${kind}|${source}|${oid}|${expression}`,
        oid,
        text: prefix + message,
        expression,
    };
}

// A mismatch comes from a function or from an Imputation default; the two
// differ in what the designer sees happen to the item, so the row must say
// which, as formatTypeMismatch does for telemetry.
const mismatchSource = (mismatch) =>
    mismatch.source === "default"
        ? EXPRESSION_SOURCE.DEFAULT_VALUE
        : EXPRESSION_SOURCE.FUNCTION;

// The hook's message already opens with "OID (label): "; the row supplies that
// itself, so the sentence is rebuilt here in the Clinic panel's words.
function mismatchSentence(mismatch) {
    const outcome =
        mismatch.source === "default"
            ? "The default was not applied."
            : "The value was cleared.";
    return `this expression returned ${mismatch.actual}, but this item needs ${mismatch.expected}. ${outcome}`;
}

export function PreviewForm() {
    const [{ form }, dispatch] = useReducer(formReducer, initialState);

    const [hasQuestModel, setHasQuestModel] = useState(false);
    const [renderError, setRenderError] = useState(null);
    // Computation results the item cannot render, keyed by item OID.
    //
    // Designer-only, and deliberately NOT folded into `renderError`: that is
    // fatal ("nothing could be rendered"), whereas these are per-item and the
    // form around them renders fine. Keyed by OID so the reactive recompute —
    // which re-reports on every keystroke — updates a row instead of appending
    // one, and so a mismatch disappears as soon as the designer fixes it.
    const [typeMismatches, setTypeMismatches] = useState({});
    // Expressions that threw, keyed by faultKey. Same panel as the mismatches:
    // until these were collected the sandbox sent them to telemetry only, and
    // a form the Clinic preview flagged looked clean here.
    const [expressionFaults, setExpressionFaults] = useState({});
    // The keys currently in `expressionFaults`, mirrored so the sink can tell
    // "never failed" from "failed and is now fixed" without touching state.
    // Every expression reports its clean runs, so on a form with no faults —
    // the normal case — this is what keeps a keystroke from queueing one no-op
    // state update per expression.
    const listedFaultKeys = useRef(new Set());
    const [page, setPage] = useState("1");
    // The form layout: paged (live behaviour) or every item group stacked on
    // one page. Owned by the Designer sidebar's "Item groups on one page"
    // toggle and driven purely by `viedoc.preview.form-layout` messages — the
    // in-frame toggle this replaced is gone, the same hoist the viewport
    // switcher went through. render-result does NOT reset it, and the parent
    // re-asserts it after every render, so the choice survives the designer's
    // constant edit-and-re-render loop either way.
    const [layout, setLayout] = useState(FORM_LAYOUT.PAGED);

    // A layout switch must land on the first group, never a stale page index
    // or the submit page. Derived from the state change itself: React bails
    // out of an equal setLayout, so the parent re-asserting an unchanged
    // layout triggers neither a re-render nor this reset.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the reset is keyed BY the layout change
    useEffect(() => {
        setPage("1");
    }, [layout]);

    // Context ref the reducer hands to validators on SET_FORM.
    const formContextRef = useRef({
        formData: {},
        dependencies: {},
        identifierModel: {},
    });

    // Keep the ref current so RangeCheck validators that read it see the
    // latest sidebar overrides.
    formContextRef.current = {
        formData: form.formData ?? {},
        dependencies: form.dependencies ?? {},
        identifierModel: form.identifierModel ?? {},
    };

    // The Designer origin that most recently drove this frame. The outbound
    // activity relay posts only here: before any valid inbound message there
    // is no designer session to keep alive, and fanning activity out to every
    // configured origin would tell bystanders about user behaviour.
    const lastDesignerOriginRef = useRef(null);

    // Mounted-once: announce readiness to the parent, then listen for
    // inbound messages. Origin / kind validation happens in the handler.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once handshake
    useEffect(() => {
        const handler = (event) => {
            if (!isAllowedOrigin(event.origin)) return;

            const data = event.data;
            if (!data || typeof data !== "object") return;
            if (!INBOUND_KINDS.has(data.kind)) return;
            // The unified preview hosts two renderers, and the shared sidebar
            // stamps every message with its addressee. Mail for the other
            // renderer must be dropped BEFORE anything acts on it — including
            // remembering its origin as the activity-relay destination.
            if (!isForMe(data)) return;

            lastDesignerOriginRef.current = event.origin;

            if (data.kind === RENDER_RESULT_KIND) {
                // Unlike the other branches, an unsupported version is
                // surfaced rather than dropped — the whole render depends on
                // this one message, so silence would strand the preview.
                if (!isSupportedVersion(data)) {
                    setRenderError(
                        `Unsupported payloadVersion ${versionOf(data)}.`
                    );
                    return;
                }

                const questModel = data.questModel;
                if (!questModel || typeof questModel !== "object") {
                    setRenderError("Missing questModel in render-result.");
                    return;
                }

                // Normalise: the reducer expects itemGroups + formData
                // + dependencies + identifierModel on the form object.
                const normalised = {
                    itemGroups: [],
                    formData: {},
                    dependencies: {},
                    identifierModel: {},
                    ...questModel,
                };

                dispatch({
                    type: types.SET_FORM,
                    payload: normalised,
                    contextRef: formContextRef,
                });
                setHasQuestModel(true);
                setRenderError(null);
                // Expressions can only change with a new render-result, so this
                // is the one point where a fixed function should stop being
                // reported. Clearing here (rather than per recompute) also means
                // a mismatch stays visible while the designer edits form DATA in
                // the sidebar, which is when they most need to see it.
                setTypeMismatches({});
                setExpressionFaults({});
                listedFaultKeys.current.clear();
                setPage("1");

                // Language travels in the envelope, not inside the questModel.
                // `availableLanguages` also arrives but is ignored: the picker
                // lives in the Designer toolbar, so the list has no consumer
                // here. The parent still sends it — the field stays part of the
                // contract for any renderer that does want to offer a choice.
                if (typeof data.language === "string" && data.language) {
                    switchSystemLanguage(data.language);
                }

                // The sidebar already holds its override snapshot when it
                // posts a render, so it sends the two together rather than
                // chasing the render with an override-change (which, for the
                // Clinic renderer, meant a second server build per render).
                // Absent from an older sidebar; then nothing to apply here.
                applyOverrides(data.overrides);
            } else if (data.kind === RENDER_ERROR_KIND) {
                setRenderError(
                    typeof data.error === "string"
                        ? data.error
                        : "Designer reported a render error."
                );
            } else if (data.kind === FORM_LAYOUT_KIND) {
                // The Designer sidebar's "Item groups on one page" toggle.
                // Idempotent state declaration: an unchanged layout bails out
                // in React, and the page reset is derived from the layout
                // change (see the effect above the handler).
                if (!isSupportedVersion(data)) return;

                const nextLayout = data.layout;
                if (!Object.values(FORM_LAYOUT).includes(nextLayout)) return;

                setLayout(nextLayout);
            } else if (data.kind === OVERRIDE_CHANGE_KIND) {
                // The hoisted Designer sidebar's override snapshot on its own:
                // the parent fans out the full current set whenever the
                // designer applies an identifier-model or cross-form-dependency
                // edit to an already rendered form.
                if (!isSupportedVersion(data)) return;
                applyOverrides(data.overrides);
            }
        };

        // One reducer action per entry, so the existing form state machine
        // treats an override the same way it would treat a direct edit. Shared
        // by render-result (snapshot riding on the render) and override-change
        // (snapshot on its own). Anything that is not an object is ignored.
        function applyOverrides(overrides) {
            if (!overrides || typeof overrides !== "object") return;

            const identifierModel = overrides.identifierModel;
            if (identifierModel && typeof identifierModel === "object") {
                for (const [name, value] of Object.entries(identifierModel)) {
                    dispatch({
                        type: types.SET_IDENTIFIER_FIELD,
                        payload: { name, value },
                    });
                }
            }

            const dependencies = overrides.dependencies;
            if (dependencies && typeof dependencies === "object") {
                for (const [oid, value] of Object.entries(dependencies)) {
                    dispatch({
                        type: types.SET_DEPENDENCY,
                        payload: { oid, value },
                    });
                }
            }
        }

        window.addEventListener("message", handler);

        // Announce readiness to every Designer origin we trust. We can't
        // securely guess the parent's origin from inside the iframe, and the
        // contract forbids `targetOrigin: "*"`, so we fan-out a `ready`
        // message to each configured origin. The parent ignores anything
        // sourced from an `event.origin` it isn't expecting, so duplicates
        // are harmless. Same-origin dev (the iframe and Designer running on
        // one host) is covered by adding our own origin when it passes the
        // allow-list check.
        const readyMessage = {
            kind: READY_KIND,
            supportedPayloadVersions: SUPPORTED_PAYLOAD_VERSIONS,
        };
        const targets = new Set(getAllowedOrigins());
        if (isAllowedOrigin(window.location.origin)) {
            targets.add(window.location.origin);
        }

        if (window.parent && window.parent !== window) {
            for (const target of targets) {
                try {
                    window.parent.postMessage(readyMessage, target);
                } catch (_e) {
                    // postMessage on a stale parent is non-fatal; swallow.
                }
            }
        }

        return () => window.removeEventListener("message", handler);
    }, []);

    // Mounted-once: relay user activity to the Designer parent, throttled.
    // The parent page hosts the IdP inactivity tracker but cannot observe
    // interaction inside this cross-origin iframe — without the relay, a
    // designer click-testing a form here for long enough is logged out
    // mid-test. Same event set the tracker itself listens for.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once listeners over refs
    useEffect(() => {
        let lastPostedAt = 0;
        const postActivity = () => {
            const origin = lastDesignerOriginRef.current;
            if (!origin) return;
            if (!window.parent || window.parent === window) return;

            const now = Date.now();
            if (now - lastPostedAt < ACTIVITY_THROTTLE_MS) return;
            lastPostedAt = now;

            try {
                window.parent.postMessage(
                    { kind: ACTIVITY_KIND, payloadVersion: 1 },
                    origin
                );
            } catch (_e) {
                // postMessage on a stale parent is non-fatal; swallow.
            }
        };

        const events = ["click", "touchstart", "keydown", "scroll", "mousemove"];
        for (const name of events) {
            window.addEventListener(name, postActivity, { passive: true });
        }
        return () => {
            for (const name of events) {
                window.removeEventListener(name, postActivity);
            }
        };
    }, []);

    // No onSubmit is wired: FormView treats an absent onSubmit as "no save
    // path", disabling Submit and short-circuiting its submit handler. Preview
    // has nothing to POST, so there is nothing to pass.

    // Navigation maps local page state only; 'home'/'events' and unknown
    // targets are no-ops because /preview has no event/home shell.
    const onNavigate = useCallback((target, arg) => {
        switch (target) {
            case "page":
                setPage(String(arg ?? 1));
                return;
            case "submit":
                setPage("submit");
                return;
            case "thank-you":
                setPage("thank-you");
                return;
            default:
                return;
        }
    }, []);

    // Stable identity: useFunctionRecompute stores this in a ref, but keeping it
    // stable also means FormView's prop doesn't change every render.
    const onComputedTypeMismatch = useCallback((mismatch) => {
        setTypeMismatches((current) => {
            // `resolved` means the item now computes an assignable value — drop
            // its row rather than leaving the panel asserting "the value was
            // cleared" above a field that is visibly filled in. Reached when the
            // designer edits data that changes the RESULT TYPE, e.g.
            // `SMOKER === "1" ? CIGS * 20 : "N/A"` on a Number item.
            if (mismatch.resolved) {
                if (!(mismatch.oid in current)) return current;
                const { [mismatch.oid]: _removed, ...rest } = current;
                return rest;
            }

            const existing = current[mismatch.oid];
            // The reactive pass re-reports the same mismatch on every keystroke.
            // Bail out on an identical message so React can skip the re-render
            // instead of replacing state with an equal value each tick.
            if (existing?.message === mismatch.message) return current;
            return { ...current, [mismatch.oid]: mismatch };
        });
    }, []);

    // Installed once for the life of the frame. Only the preview installs a
    // sink, so the live patient flow keeps showing a participant nothing.
    //
    // Visibility conditions run inside formReducer, i.e. while this component
    // renders, so the sink runs during render. Both the ref writes and the
    // state updates below are safe there: the ref holds a Set, so a repeated
    // render applies the same add or delete again with no effect, and every
    // state branch returns `current` when nothing changed. Moving the reducer
    // into a child would turn this into a cross-component render-phase update.
    useEffect(
        () =>
            setDesignFaultSink((report) => {
                const key = faultKey(report);

                if (report.resolved) {
                    // An expression that never failed still reports every
                    // clean run, so this is the path taken by most reports.
                    // Answering from the ref keeps it free.
                    if (!listedFaultKeys.current.has(key)) return;
                    listedFaultKeys.current.delete(key);
                    setExpressionFaults((current) => {
                        if (!(key in current)) return current;
                        const { [key]: _dropped, ...rest } = current;
                        return rest;
                    });
                    return;
                }

                listedFaultKeys.current.add(key);
                setExpressionFaults((current) => {
                    // Same expression, same error: the re-evaluation on every
                    // keystroke must not re-render. A different error for the
                    // same expression replaces the row, since sidebar data can
                    // move an expression from one failure to another.
                    if (current[key]?.message === report.message) return current;
                    return { ...current, [key]: report };
                });
            }),
        []
    );

    const rows = [
        ...Object.values(typeMismatches).map((m) =>
            toPanelRow(
                "type-mismatch",
                m.oid,
                m.label,
                mismatchSource(m),
                mismatchSentence(m),
                m.expression
            )
        ),
        ...Object.values(expressionFaults).map((f) =>
            toPanelRow(
                "expression-error",
                f.oid,
                f.label,
                f.source,
                f.message,
                f.expression
            )
        ),
    ];

    const previewModeValue = useMemo(() => ({ mode: RenderMode.PREVIEW }), []);

    return (
        <PreviewModeContext.Provider value={previewModeValue}>
            {/*
              CSP `frame-ancestors` cannot be set from a <meta http-equiv>
              tag — UAs ignore that directive in meta per the CSP spec.
              The production reverse proxy MUST set
                Content-Security-Policy: frame-ancestors <designer origins>
              and drop `X-Frame-Options: DENY` for /preview. See
              docs/features/2-preview-route.md → "Operational notes".
            */}
            <div className="preview-form" data-testid="preview-form">
                {renderError ? (
                    <div
                        className="preview-form__error"
                        role="alert"
                        data-testid="preview-form-error"
                    >
                        {renderError}
                    </div>
                ) : null}

                {/* The full app chrome, as the participant sees it: the sticky
                    Me header above the form and the footer below, both inert
                    replicas (see PreviewAppHeader / PreviewAppFooter). Every
                    in-frame preview control is gone — language, viewport and
                    the item-groups layout all live in the Designer parent's
                    sidebar, which narrows the iframe from the outside and
                    drives the layout over viedoc.preview.form-layout. */}
                <PreviewAppHeader />

                <main className="preview-form__main">
                    <div
                        className="preview-form__pane"
                        data-layout={layout}
                        data-testid="preview-form-pane"
                    >
                        {hasQuestModel ? (
                            <>
                                {rows.length > 0 ? (
                                    <div
                                        className="preview-form__diagnostics"
                                        role="alert"
                                        data-testid="preview-form-diagnostics"
                                    >
                                        <p className="preview-form__diagnostics-title">
                                            {rows.length === 1
                                                ? "One expression problem in this form"
                                                : `${rows.length} expression problems in this form`}
                                        </p>
                                        <ul className="preview-form__diagnostics-list">
                                            {rows.map((row) => (
                                                <li
                                                    key={row.key}
                                                    className="preview-form__diagnostics-item"
                                                    data-oid={row.oid}
                                                >
                                                    {row.text}
                                                    {/* The expression is what
                                                        the designer has to go
                                                        back and change, so it
                                                        is shown verbatim. */}
                                                    <code className="preview-form__diagnostics-expression">
                                                        {row.expression}
                                                    </code>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}
                                <FormView
                                    page={page}
                                    form={form}
                                    dispatch={dispatch}
                                    onNavigate={onNavigate}
                                    onComputedTypeMismatch={onComputedTypeMismatch}
                                    isLoading={false}
                                    isSending={false}
                                    error={null}
                                    layout={layout}
                                />
                            </>
                        ) : (
                            <div
                                className="preview-form__waiting"
                                data-testid="preview-form-waiting"
                            >
                                Waiting for designer…
                            </div>
                        )}
                    </div>
                </main>

                <PreviewAppFooter />
            </div>
        </PreviewModeContext.Provider>
    );
}
